import { Vector3 } from "three";
import { switchMap, first } from "rxjs/operators";
import { DisparityTile, isDefined, PrecisionUtils, QuadtreeUtils } from "../../infra3d";
import { CubemapGeoFrameCollection } from "../geoframe/cubemapgeoframecollection";
import { GeoFrame } from "../geoframe/geoframe";
import { MeasureHandler, MeasurePoint } from "./internal";
/**
 * Measure point representation
 * @author Joel Burkhard
 * @class PanoMeasurePoint
 * @extends MeasurePoint
 * @constructor
 */
export class PanoMeasurePoint extends MeasurePoint {
    constructor(geoframe, mouseX, mouseY, maxdist, successcbf, failedcbf) {
        super();
        this.disparity = 0.0;
        this._successcbf = null;
        this._failedcbf = null;
        this._measurehandler = null;
        this._disparitytiles = {};
        this.pending = false;
        this.depthsearchpending = false;
        this.offsetEasting = 0;
        this.offsetNorthing = 0;
        this.offsetHeight = 0;
        this.mouseX = 0;
        this.mouseY = 0;
        this._imageX = 0;
        this._imageY = 0;
        this._disparityInmm = -1;
        this._geoframeDepth = null;
        this._modelEasting = 0;
        this._modelNorthing = 0;
        this._modelHeight = 0;
        this._sensorid = 0;
        this.geoframe = geoframe;
        this.mouseX = mouseX;
        this.mouseY = mouseY;
        this.maxdist = maxdist || 20;
        this._successcbf = successcbf || null;
        this._failedcbf = failedcbf || null;
        this._measurehandler = geoframe.parentGeoFrameCollection.scene.getMeasureHandler();
        this.warning = true;
        this.pending = false;
        this.depthsearchpending = false;
        this.offsetEasting = geoframe.easting;
        this.offsetNorthing = geoframe.northing;
        this.offsetHeight = geoframe.height;
        this.Omega = (geoframe.omega * Math.PI) / 180;
        this.Phi = (geoframe.phi * Math.PI) / 180;
        this.Kappa = (geoframe.kappa * Math.PI) / 180;
        this.calcCoordinates(mouseX, mouseY);
    }
    /**
     * Calculates the coordinates out of the mouse position
     *
     * @param {number} mouseX - from canvas coordinates canvas left-top is origin !
     * @param {number} mouseY - from canvas coordinates canvas left-top is origin !
     */
    calcCoordinates(mouseX, mouseY) {
        //Pick map coordinates from pano-callote
        const pano_map = this._measurehandler.mouseToMap(mouseX, mouseY, this.geoframe, false);
        if (pano_map.length > 0) {
            if (isDefined(pano_map[0].coords)) {
                this.calcCoordinatesFromMap(pano_map[0].coords, pano_map[0].sensorid);
            }
            else {
                this.warning = true;
                this._failedcbf ? this._failedcbf("no map-coords in pano picked") : null;
            }
        }
        else {
            this.warning = true;
            this._failedcbf ? this._failedcbf("no map-coords in pano picked") : null;
        }
    }
    calcCoordinatesFromMap(map, sensorid) {
        //Calculate normalized Image-Coordinates (x*,y*) --> see Abraham/Foerstner
        const pano_image = this._measurehandler.sensorToImage(this._measurehandler.mapToSensor(map, this.geoframe), this.geoframe);
        //Calculate Normalized Model-Coordinates (X,Y,Z) --> see Abraham/Foerstner
        const r_cPano_p = this._measurehandler.imageToModel(pano_image, 1, sensorid, this.geoframe).normalize();
        //Calcualtion of to points on the picked Pano-Ray: One in 2m-Distance, and one in maxdist-Distance.
        const r_m_p1 = this._measurehandler.modelToWorld(r_cPano_p.multiplyScalar(2.0), sensorid, this.geoframe);
        const r_m_p2 = this._measurehandler.modelToWorld(r_cPano_p.multiplyScalar(this.maxdist / 2.0), sensorid, this.geoframe);
        //set proximate position
        this.Easting = r_m_p2.x;
        this.Northing = r_m_p2.y;
        this.Height = r_m_p2.z;
        if (!this.geoframe.parentGeoFrameCollection.isRunning) {
            //Go further to search a position on the section between these to points.
            this._searchPosition(r_m_p1, r_m_p2);
        }
        else {
            this.warning = true;
            this._failedcbf ? this._failedcbf("stream is playing") : null;
            return;
        }
    }
    _searchPosition(p1, p2) {
        this.pending = true;
        this._searchDepthMapCandidates((geoframes) => {
            this.pending = false;
            for (let i = 0; i < geoframes.length; i++) {
                const geoframe = geoframes[i];
                let camids = [0];
                let cubefaces = [0];
                if (geoframe.type == "cubemap") {
                    camids = [0, 1, 2, 3, 4, 5];
                    cubefaces = [];
                    for (const cubeface of CubemapGeoFrameCollection.cubefaces) {
                        cubefaces.push(cubeface.position);
                    }
                }
                for (let j = 0; j < camids.length; j++) {
                    if (!this.warning)
                        return;
                    //transfrom Point 1 from world-coordinates to geoframe-model-coordinates
                    const r_c_p1 = this._measurehandler.worldToModel(p1, j, geoframe, cubefaces[j]);
                    //check if point 1 is possibly visible in the frame.
                    if (r_c_p1.z < 0.0) {
                        //transfrom Point 2 from world-coordinates to geoframe-model-coordinates
                        const r_c_p2 = this._measurehandler.worldToModel(p2, j, geoframe, cubefaces[j]);
                        //transform the model-coordinates of point 1 and 2 to sensor-coordinates (u,v in pixel)
                        const p1_uv = this._measurehandler.imageToSensor(this._measurehandler.modelToImage(r_c_p1, j, geoframe), geoframe);
                        const p2_uv = this._measurehandler.imageToSensor(this._measurehandler.modelToImage(r_c_p2, j, geoframe), geoframe);
                        const width = geoframe.getMeasureInfo().imagemeta.width;
                        const height = geoframe.getMeasureInfo().imagemeta.height;
                        //check if one of the point lies within the image.
                        if ((p1_uv.x > 0 && p1_uv.x < width && p1_uv.y > 0 && p1_uv.y < height) ||
                            (p2_uv.x > 0 && p2_uv.x < width && p2_uv.y > 0 && p2_uv.y < height)) {
                            //calculate the elements of a linear-equation with the two points. (Elemente der Geradengleichung)
                            const r_p1_p2 = new Vector3(r_c_p2.x, r_c_p2.y, r_c_p2.z);
                            r_p1_p2.sub(r_c_p1);
                            const l = r_p1_p2.length();
                            r_p1_p2.normalize();
                            //r_c_p1 = Ortsvektor r
                            //r_p1_p2 = Richtungsvektor v
                            //l = Laenge in m
                            const pickedvector = {
                                r: r_c_p1,
                                v: r_p1_p2,
                                l: l,
                            };
                            this.depthsearchpending = true;
                            this._searchDepth(pickedvector, geoframe, j, cubefaces[j]);
                        }
                    }
                }
            }
            if (this.warning && !this.depthsearchpending) {
                this._failedcbf ? this._failedcbf("failed") : null;
            }
        });
        //pending is true as long as mmscript request is active to get new candidategeoframes from other streams
        if (this.warning && !this.pending) {
            this._failedcbf ? this._failedcbf("failed") : null;
        }
    }
    /**
     * Returns all stereosystem-geoframes from the same epoch as the current geoframe from the panorama-system.
     * @param cbf
     * @private
     */
    _searchDepthMapCandidates(cbf) {
        const scene = this.geoframe.parentGeoFrameCollection.scene;
        const navigator = scene.navigator;
        navigator.stateService.currentNode$
            .pipe(first(), switchMap((node) => navigator.cameraService.cacheCameras$(node)))
            .subscribe((allnodes) => {
            const cubenodes = allnodes.filter((x) => {
                return x.frame.streamtype == "cubemap";
            });
            const stereonodes = allnodes.filter((x) => {
                return x.frame.streamtype == "stereo";
            });
            let resnodes = cubenodes;
            if (cubenodes.length == 0) {
                resnodes = stereonodes;
            }
            const resframes = resnodes.map((x) => {
                return new GeoFrame(scene, x.frame);
            });
            cbf(resframes);
        });
    }
    /**
     * instersects of a vector with the depthmap of the given geoframe
     * @param pickedvector
     * @param geoframe
     * @param camid
     * @private
     */
    _searchDepth(pickedvector, geoframe, camid = 0, cubeface = 0) {
        const mi = geoframe.getMeasureInfo();
        let prev_z = 0;
        let prev_d = 0;
        const step = 0;
        let stepsize = 0.1;
        const tilesearch = (step) => {
            //calculate a point r with distance "step" on the picked vector
            const r = new Vector3();
            r.add(pickedvector.v);
            r.multiplyScalar(step);
            r.add(pickedvector.r);
            const imgcoor = this._measurehandler.modelToImage(r, camid, geoframe);
            this._imageX = imgcoor.x;
            this._imageY = imgcoor.y;
            //calculate map-coordinates of the point (nx,ny)
            const map_xy = this._measurehandler.sensorToMap(this._measurehandler.imageToSensor(imgcoor, geoframe), geoframe);
            //Berechnung Quadcode und Laden des entsprechenden Tiles
            const quadcode = QuadtreeUtils.NormalizedCoordToQuadKey(map_xy.x, map_xy.y, mi.depthmapmeta.maxzoom);
            //request a tile for the quadcode
            this._getDisparityTileFromQuadcode(quadcode, geoframe, (tile) => {
                let depth = 0;
                let z = 0;
                if (tile.isAvailable()) {
                    //get the disparitymap-value out of the tile with the given map-coordinates.
                    const dispmapvalue = tile.getDisparityValue(map_xy.x, map_xy.y);
                    //calcualte the depth in mm
                    const disp = ((dispmapvalue[0] * mi.depthmapmeta.numdisparities) / 65535) *
                        mi.depthmapmeta.pixsize *
                        0.001;
                    depth = (mi.depthmapmeta.focallength * mi.depthmapmeta.basis) / disp;
                    z = r.z * -1;
                    //check if the depth intersects the vector
                    if (z > depth && prev_z < prev_d) {
                        //calculate world-coordinates for the found point r
                        const frame_coords = this._measurehandler.modelToFrame(r, camid, geoframe, cubeface);
                        const r_m_p = this._measurehandler.frameToWorld(frame_coords, geoframe);
                        this.Easting = r_m_p.x;
                        this.Northing = r_m_p.y;
                        this.Height = r_m_p.z;
                        this.quality = dispmapvalue[1];
                        this._modelEasting = r.x;
                        this._modelNorthing = r.y;
                        this._modelHeight = r.z;
                        this._disparityInmm = disp;
                        this._geoframeDepth = geoframe;
                        this._sensorid = camid;
                        this.frameX = frame_coords.x;
                        this.frameY = frame_coords.y;
                        this.frameZ = frame_coords.z;
                        this._calcPrecision();
                        this.warning = false;
                        this._successcbf ? this._successcbf(this) : null;
                        step = pickedvector.l;
                        return;
                    }
                    else {
                        //bigger steps if too far (to avoid maxCallstackSize exceeded error)
                        if (depth == Infinity)
                            stepsize = 1;
                        //make step smaller the closer you get
                        if (depth - z < stepsize && stepsize != 0.01)
                            stepsize /= 10;
                    }
                }
                else {
                    this.warning = true;
                    this._failedcbf ? this._failedcbf("failed") : null;
                    return;
                }
                prev_d = depth;
                prev_z = z;
                if (step < pickedvector.l) {
                    tilesearch((step += stepsize));
                }
                else {
                    this.warning = true;
                    this._failedcbf ? this._failedcbf("failed") : null;
                    return;
                }
            }, (tile) => {
                this.warning = true;
                this._failedcbf ? this._failedcbf("failed") : null;
                return;
            }, camid);
        };
        tilesearch(step);
    }
    _getDisparityTileFromQuadcode(quadcode, geoframe, cbf, cbf_failed, camid = 0) {
        let tile = this._disparitytiles[quadcode];
        if (isDefined(tile)) {
            cbf(tile);
        }
        else {
            const mi = geoframe.getMeasureInfo();
            const quadtreesize = Math.pow(2, mi.depthmapmeta.maxzoom) * mi.imagemeta.tilesize;
            tile = new DisparityTile(quadcode, geoframe.dataPath + "/" + camid, mi.depthmapmeta.width / quadtreesize, mi.depthmapmeta.height / quadtreesize, mi.depthmapmeta.tilesize, this.geoframe.parentGeoFrameCollection.infrascene.traversalState, (tile) => {
                this._disparitytiles[quadcode] = tile;
                cbf(tile);
            }, (tile_empty) => {
                cbf_failed(tile_empty);
            }, camid);
            if (tile.noData)
                cbf_failed(tile);
            this._disparitytiles[quadcode] = tile;
        }
    }
    checkCoordinates() {
        this.warntext = "";
        this.warning = false;
        if (this.maxdist == null) {
            this.warning = true;
            this.warntext = "No maximal range value set !";
            return;
        }
        const d = Math.sqrt(Math.pow(this["Easting"] - this.offsetEasting, 2) +
            Math.pow(this["Northing"] - this.offsetNorthing, 2) +
            Math.pow(this["Height"] - this.offsetHeight, 2));
        this.distance = d;
        if (d > this.maxdist) {
            this.warning = true;
            this.warntext = "Position too far from projection center";
            return;
        }
        if (!this.isValid()) {
            this.warning = true;
            this.warntext = "Inavlid Values in Northing/Easting/Height";
            return;
        }
    }
    /**
     * returns the point values as vec3 object.
     * @return {MeasurePoint}
     */
    copy() {
        const pointcopy = new PanoMeasurePoint(this.geoframe, this.mouseX, this.mouseY, this.maxdist);
        pointcopy["Height"] = this["Height"];
        pointcopy["Easting"] = this["Easting"];
        pointcopy["Northing"] = this["Northing"];
        return pointcopy;
    }
    _calcPrecision() {
        this._calcPrecisionLocal();
        this._calcPrecisionLocalGeoFrame();
        this._calcPrecisionLocalGlobal();
        this._calcPrecisionGlobal();
    }
    _calcPrecisionLocal() {
        const measureinfo = this.geoframe.getMeasureInfo();
        const precX = measureinfo.precDisparity * measureinfo.imagemeta.pixsize * 0.001;
        let precP = measureinfo.precDisparity * measureinfo.depthmapmeta.pixsize * 0.001;
        if (this.quality === 64)
            precP = 2.0 * precP;
        // NOTE: All units in [mm].
        const args = {
            x: this._imageX,
            y: this._imageY,
            p: this._disparityInmm,
            c: measureinfo.imagemeta.focallength,
            b: this._geoframeDepth.getMeasureInfo().depthmapmeta.basis * 1000.0,
            precX: precX,
            precY: precX,
            precP: precP,
            precC: measureinfo.precFocallength,
            precB: measureinfo.precBasis,
        };
        const r = PrecisionUtils.pointLocal(args);
        // NOTE: All units in [m].
        (this.precLocalX = r.x * 0.001), (this.precLocalY = r.y * 0.001);
        this.precLocalZ = r.z * 0.001;
    }
    _calcPrecisionGlobal() {
        const precLocal = this._precisionLocalGeoFrame();
        const args = {
            x: this._modelEasting, // [m]
            y: this._modelNorthing, // [m]
            z: this._modelHeight, // [m]
            omega: this.Omega, // [rad]
            phi: this.Phi, // [rad]
            kappa: this.Kappa, // [rad]
            precX: precLocal.x * 1000.0, // [mm]
            precY: precLocal.y * 1000.0, // [mm]
            precZ: precLocal.z * 1000.0, // [mm]
            precOmega: (this.geoframe.precOmega * Math.PI) / 180, // [rad]
            precPhi: (this.geoframe.precPhi * Math.PI) / 180, // [rad]
            precKappa: (this.geoframe.precOmega * Math.PI) / 180, // [rad]
            precTx: this.geoframe.precEasting * 1000.0, // [mm]
            precTy: this.geoframe.precNorthing * 1000.0, // [mm]
            precTz: this.geoframe.precHeight * 1000.0, // [mm]
        };
        const r = PrecisionUtils.pointGlobal(args);
        // NOTE: All units in [m].
        (this.precGlobalEasting = r.x * 0.001), (this.precGlobalNorthing = r.y * 0.001);
        this.precGlobalHeight = r.z * 0.001;
    }
    _calcPrecisionLocalGeoFrame() {
        const precLocal = this._precisionLocalGeoFrame();
        this.precFrameX = precLocal.x;
        this.precFrameY = precLocal.y;
        this.precFrameZ = precLocal.z;
    }
    _calcPrecisionLocalGlobal() {
        const precLocal = this._precisionLocalGeoFrame();
        const R = MeasureHandler.getRotationMatrix((this.geoframe.omega * Math.PI) / 180, (this.geoframe.phi * Math.PI) / 180, (this.geoframe.kappa * Math.PI) / 180);
        precLocal.applyMatrix4(R);
        this.precLocalEasting = Math.abs(precLocal.x);
        this.precLocalNorthing = Math.abs(precLocal.y);
        this.precLocalHeight = Math.abs(precLocal.z);
    }
    _precisionLocalGeoFrame() {
        // NOTE: Transform local precisions to sensorarray /
        // geoframe.
        const precLocal = new Vector3(this.precLocalX, this.precLocalY, this.precLocalZ);
        const minfo = this.geoframe.getMeasureInfo();
        const sensorpos = minfo.sensors[this._sensorid].position;
        const R = MeasureHandler.getRotationMatrix((sensorpos.domega * Math.PI) / 180, (sensorpos.dphi * Math.PI) / 180, (sensorpos.dkappa * Math.PI) / 180);
        precLocal.applyMatrix4(R);
        return precLocal;
    }
}
//# sourceMappingURL=panomeasurepoint.js.map